import { AfterViewInit, Component, inject, Input } from '@angular/core';
import { TrialInfoService } from "../../../../core/services/trial-info.service";
import { FormControl, FormGroup } from "@angular/forms";
import { BaseComponent } from "../../../../shared/base-component";
import { LogHelper } from "../../../../core/helpers/log.helper";
import {
  TrialInformation,
  TrialInformationTranslation
} from "../../../../core/models/trial-info/trial-information.model";
import { Cultures } from "../../../../core/constants/cultures";
import { requiredNoWhitespaceValidator } from "../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-trial-information',
  templateUrl: './trial-information.component.html',
  styleUrl: './trial-information.component.scss'
})
export class TrialInformationComponent extends BaseComponent implements AfterViewInit {
  @Input() trialId: string;
  private readonly _trialInfoService = inject(TrialInfoService);
  form = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    selectedCulture: new FormControl<string>('en'),
    introductionEnabled: new FormControl<boolean>(false),
    trialInformationEnabled: new FormControl<boolean>(false)
  });
  cultureForms: FormGroup[] = [];
  availableCultures: { value: string, text: string }[] = Cultures.all().map(c => ({ value: c.culture, text: c.name }));
  filteredCultures: { value: string, text: string }[] = this.availableCultures;
  ngAfterViewInit(): void {
    Promise.resolve().then(() => this.loadTrialInfo());
    // Update validation when introduction enabled toggle has been updated
    this.form.get('introductionEnabled').valueChanges.subscribe(value => {
      this.onToggleChanged();
    });
    // Update validation when trial information enabled toggle has been updated
    this.form.get('trialInformationEnabled').valueChanges.subscribe(value => {
      this.onToggleChanged();
    });
  }
  loadTrialInfo() {
    if (!this.trialId)
      return;
    this.form.patchValue({isBusy: true});
    this._trialInfoService.getTrialInformation(this.trialId).subscribe({
      next: trialInfo => {
        this.populateAndBuildForms(trialInfo);
      },
      error: err => {
        LogHelper.log(err);
        this._alertService.showErrorAlert(err);
      }
    });
  }
  populateAndBuildForms(trialInfo: TrialInformation) {
    this.form.patchValue({
      isBusy: false,
      introductionEnabled: trialInfo.introductionEnabled,
      trialInformationEnabled: trialInfo.trialInformationEnabled
    });
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = trialInfo.translations.find(t => t.culture === culture.value);
      let form = new FormGroup({
        culture: new FormControl<string>(culture.value),
        introductionTitle: new FormControl<string>(translation?.introductionTitle, trialInfo.introductionEnabled && culture.value === 'en' ? requiredNoWhitespaceValidator() : null),
        introductionMessage: new FormControl<string>(translation?.introductionMessage, trialInfo.introductionEnabled && culture.value === 'en' ? requiredNoWhitespaceValidator() : null),
        trialInformation: new FormControl<string>(translation?.trialInformation, trialInfo.trialInformationEnabled && culture.value === 'en' ? requiredNoWhitespaceValidator() : null)
      });
      this.cultureForms.push(form);
    });
  }
  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();
    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }
  onToggleChanged() {
    let englishIndex = this.cultureForms.findIndex(f => f.value.culture === 'en');
    if (englishIndex === -1)
      return;
    setTimeout(() => {
      this.cultureForms[englishIndex].get('introductionTitle').setValidators(this.form.value.introductionEnabled ? requiredNoWhitespaceValidator() : null);
      this.cultureForms[englishIndex].get('introductionMessage').setValidators(this.form.value.introductionEnabled ? requiredNoWhitespaceValidator() : null);
      this.cultureForms[englishIndex].get('trialInformation').setValidators(this.form.value.trialInformationEnabled ? requiredNoWhitespaceValidator() : null);
      this.cultureForms[englishIndex].get('introductionTitle').updateValueAndValidity();
      this.cultureForms[englishIndex].get('introductionMessage').updateValueAndValidity();
      this.cultureForms[englishIndex].get('trialInformation').updateValueAndValidity();
    }, 200);
  }
  cultureFormsValid() {
    return this.cultureForms.every(cultureForm => cultureForm.valid);
  }
  formsTouched() {
    return this.cultureForms.some(cultureForm => cultureForm.touched) || this.form.touched;
  }
  markFormsUntouched() {
    this.form.markAsUntouched();
    this.cultureForms.forEach(cultureForm => {
      cultureForm.markAsUntouched();
    });
  }
  saveButtonDisabled() {
    return !this.cultureFormsValid() || !this.formsTouched();
  }
  onSubmit() {
    if (!this.cultureFormsValid())
      return;
    this.form.patchValue({isBusy: true});
    let dto = new TrialInformation({
      trialInformationEnabled: this.form.value.trialInformationEnabled,
      introductionEnabled: this.form.value.introductionEnabled,
      translations: this.cultureForms.map(form => new TrialInformationTranslation({
        culture: form.value.culture,
        introductionTitle: form.value.introductionTitle,
        introductionMessage: form.value.introductionMessage,
        trialInformation: form.value.trialInformation
      }))
    });
    this._trialInfoService.updateTrialInformation(this.trialId, dto).subscribe({
      next: trialInfo => {
        this.form.patchValue({isBusy: false});
        this.populateAndBuildForms(trialInfo);
        this.markFormsUntouched();
        this._alertService.showSuccessAlert("Trial information has been successfully updated.");
      },
      error: err => {
        LogHelper.log(err);
        this.form.patchValue({isBusy: false});
        this._alertService.showErrorAlert(err);
      }
    })
  }
}
